import type { ListboxFieldOption } from "@/components/flowbite/forms/fields/listbox/types";
import type { IDistrictMetadataDate, IDistrictStudentGradeLevel , IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import type {
  IAdvancedFilterOption,
  IAdvancedFilterOptions,
} from "@/providers/store/api/shapes/interfaces/shapes.interface";
import { StudentImportType } from "@/providers/store/api/students/students.interface";

import {
  DEFAULT_FILTERS,
  MONTH_NAMES,
  PLOTTING_FILTER_NONE_VALUE,
  plottingSchoolTypeNames,
} from "../_constants/plotting-filters.constants";
import { PlottingSchoolTypeEnum } from "../_enums/plotting-filters.enum";

import {
  getOrphanAttendanceSchoolInternalId,
  NO_ATTENDANCE_SCHOOL_INFO_SENTINEL,
} from "./resolve-plotting-school-filter-params";

import type { AdvancedFilterFormValue, PlottingFiltersFormValues } from "../_schemas/plotting-filters.schema";

export type PlottingGradeListboxOption = ListboxFieldOption & { orderIndex: number };

export interface ParsedPlottingImportPeriod {
  year: number;
  snapshotType: string;
  month?: number;
}

export function parsePlottingImportPeriod(importPeriod: string): ParsedPlottingImportPeriod | null {
  if (!importPeriod) {
    return null;
  }

  const parts = importPeriod.split("-");
  const year = parseInt(parts[0] ?? "", 10);
  const snapshotType = parts[2] ?? "";
  if (Number.isNaN(year) || !snapshotType) {
    return null;
  }

  const parsedMonth = parseInt(parts[1] ?? "", 10);

  return {
    year,
    snapshotType,
    month: Number.isNaN(parsedMonth) ? undefined : parsedMonth,
  };
}

/**
 * Advanced filter options to show for the selected import period. Takes the query's `currentData`
 * (only ever the selected period's response) and shows none while fetching, so another period's
 * values are never offered as this period's options.
 */
export function resolveAdvancedFilterOptionsState(
  currentData: IAdvancedFilterOptions | undefined,
  isFetching: boolean,
): { isLoading: boolean; filterOptions: IAdvancedFilterOption[] } {
  if (isFetching) {
    return { isLoading: true, filterOptions: [] };
  }

  return { isLoading: false, filterOptions: currentData?.filters ?? [] };
}

/** Advanced filter values belong to one import period's data, so selecting another period clears them. */
export function getDraftAdvancedFiltersForImportPeriodChange(
  previousImportPeriod: string,
  nextImportPeriod: string,
  draftAdvancedFilters: AdvancedFilterFormValue[],
): AdvancedFilterFormValue[] {
  return previousImportPeriod === nextImportPeriod ? draftAdvancedFilters : [];
}

export interface ISchoolTypeDefaultGradeCodes {
  fromCode: number;
  toCode: number;
}

/** Full filter snapshot from a preset/LS entry */
export function buildFiltersFromPresetSnapshot(
  presetFilters: PlottingFiltersFormValues,
): PlottingFiltersFormValues {
  return {
    ...DEFAULT_FILTERS,
    ...presetFilters,
    attendingSchoolIds: presetFilters.attendingSchoolIds ?? [],
    advancedFilters: presetFilters.advancedFilters ?? [],
    ...(presetFilters.customAttributeFilters != null
      ? { customAttributeFilters: presetFilters.customAttributeFilters }
      : {}),
  };
}

/**
 * Validates if a year value is valid against available options
 */
export function isYearValid(year: string, options: ListboxFieldOption[]): boolean {
  if (!year || year === PLOTTING_FILTER_NONE_VALUE) return true;
  return options.some((opt) => opt.value === year);
}

/**
 * Validates if a school type value is valid against available options
 */
export function isSchoolTypeValid(schoolType: string, options: ListboxFieldOption[]): boolean {
  if (!schoolType) return false;
  return options.some((opt) => opt.value === schoolType);
}

/**
 * Validates if an import period value is valid against available options
 */
export function isImportPeriodValid(
  importPeriod: string,
  options: ListboxFieldOption[],
): boolean {
  return (
    !importPeriod ||
    importPeriod === PLOTTING_FILTER_NONE_VALUE ||
    options.some((opt) => opt.value === importPeriod)
  );
}

/**
 * Validates if saved plotting filters are still valid against current backend options.
 * Returns false if any saved value (importPeriod, schoolType, fromGrade, toGrade) is no longer in options.
 */
export function areSavedPlottingFiltersValid(
  saved: PlottingFiltersFormValues,
  importPeriodOptions: ListboxFieldOption[],
  schoolTypeOptions: ListboxFieldOption[],
  gradeOptions: PlottingGradeListboxOption[],
  districtSchools?: IDistrictSchoolListItem[] | null,
): boolean {
  if (!isImportPeriodValid(saved.importPeriod, importPeriodOptions)) return false;

  if (!isSchoolTypeValid(saved.schoolType, schoolTypeOptions)) return false;
  if (!areGradesValid(saved.fromGrade, saved.toGrade, gradeOptions)) return false;

  if (saved.schoolType === PlottingSchoolTypeEnum.INDIVIDUAL) {
    const ids = saved.attendingSchoolIds ?? [];
    if (ids.length === 0) return false;

    if (districtSchools?.length) {
      const validIds = new Set(districtSchools.map((school) => school.id));
      const hasValidSelection = ids.some(
        (id) =>
          validIds.has(id) ||
          id === NO_ATTENDANCE_SCHOOL_INFO_SENTINEL ||
          getOrphanAttendanceSchoolInternalId(id) !== null,
      );
      if (!hasValidSelection) return false;
    }
  }

  return true;
}

/**
 * Validates if grade values are valid and in correct order
 */
export function areGradesValid(fromGrade: string, toGrade: string, options: PlottingGradeListboxOption[]): boolean {
  if (
    !fromGrade ||
    fromGrade === PLOTTING_FILTER_NONE_VALUE ||
    !toGrade ||
    toGrade === PLOTTING_FILTER_NONE_VALUE
  ) {
    return true;
  }

  const fromOpt = options.find((opt) => String(opt.value) === fromGrade);
  const toOpt = options.find((opt) => String(opt.value) === toGrade);
  if (!fromOpt || !toOpt) return false;

  return toOpt.orderIndex >= fromOpt.orderIndex;
}

/**
 * Creates import period options from district metadata date data.
 * Each (year, month, type) is a distinct option. Value format: year-month-type.
 * FALL_SNAPSHOT or month=0 is labeled "Full".
 */
export function createImportPeriodOptions(dateData: IDistrictMetadataDate[] | null | undefined): ListboxFieldOption[] {
  if (!dateData || dateData.length === 0) {
    return [{ value: PLOTTING_FILTER_NONE_VALUE, label: "None" }];
  }

  const sorted = [...dateData].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return a.month - b.month;
  });

  return sorted.map(({ year, month, type }) => {
    const label =
      type === StudentImportType.FALL_SNAPSHOT || month === 0
        ? `${year}`
        : `${year} (${MONTH_NAMES[month - 1]})`;
    return {
      value: `${year}-${month}-${type}`,
      label,
    };
  });
}

/**
 * Creates school type options from district metadata
 */
export function createSchoolTypeOptions(schoolTypes: string[] | null | undefined): ListboxFieldOption[] {
  const options: ListboxFieldOption[] = [
    { value: PlottingSchoolTypeEnum.ALL, label: plottingSchoolTypeNames[PlottingSchoolTypeEnum.ALL] },
  ];

  if (!schoolTypes || schoolTypes.length === 0) {
    return options;
  }

  const availableTypes = schoolTypes;
  const typeOrder = [
    PlottingSchoolTypeEnum.K8,
    PlottingSchoolTypeEnum.ELEMENTARY,
    PlottingSchoolTypeEnum.MIDDLE,
    PlottingSchoolTypeEnum.HIGH,
  ];

  typeOrder.forEach((type) => {
    if (availableTypes.includes(type)) {
      options.push({
        value: type,
        label: plottingSchoolTypeNames[type],
      });
    }
  });

  options.push({
    value: PlottingSchoolTypeEnum.INDIVIDUAL,
    label: plottingSchoolTypeNames[PlottingSchoolTypeEnum.INDIVIDUAL],
  });

  return options;
}

/**
 * Creates grade options from district metadata (`label`, `orderIndex`, primary `code` for tile/API params).
 */
export function createGradeOptions(
  levels: IDistrictStudentGradeLevel[] | null | undefined,
): PlottingGradeListboxOption[] {
  if (!levels || levels.length === 0) {
    return [{ value: PLOTTING_FILTER_NONE_VALUE, label: "None", orderIndex: -999 }];
  }

  const sorted = [...levels].sort((a, b) => a.orderIndex - b.orderIndex);

  return sorted.map((lvl) => ({
    value: String(lvl.code),
    label: lvl.label,
    orderIndex: lvl.orderIndex,
  }));
}

/**
 * Default grade spans by school type (primary codes). K8 and elementary start at TK (21).
 */
export function getDefaultGradeCodesForSchoolType(type: PlottingSchoolTypeEnum): ISchoolTypeDefaultGradeCodes | null {
  if (type === PlottingSchoolTypeEnum.ALL) {
    return null;
  }

  switch (type) {
    case PlottingSchoolTypeEnum.K8:
      return { fromCode: 21, toCode: 8 };
    case PlottingSchoolTypeEnum.ELEMENTARY:
      return { fromCode: 21, toCode: 6 };
    case PlottingSchoolTypeEnum.MIDDLE:
      return { fromCode: 6, toCode: 9 };
    case PlottingSchoolTypeEnum.HIGH:
      return { fromCode: 9, toCode: 12 };
    default:
      return null;
  }
}

export function findGradeOptionByCode(
  options: PlottingGradeListboxOption[],
  code: number,
): PlottingGradeListboxOption | undefined {
  return options.find(
    (opt) => opt.value !== PLOTTING_FILTER_NONE_VALUE && Number(opt.value) === code,
  );
}

function resetIndividualSchoolSelection(): Pick<PlottingFiltersFormValues, "schoolType" | "attendingSchoolIds"> {
  return {
    schoolType: PlottingSchoolTypeEnum.ALL,
    attendingSchoolIds: [],
  };
}

function resolveIndividualSchoolTypeDefaults(
  currentFilters: PlottingFiltersFormValues,
  districtSchools?: IDistrictSchoolListItem[] | null,
): Partial<PlottingFiltersFormValues> {
  if (currentFilters.schoolType !== PlottingSchoolTypeEnum.INDIVIDUAL) {
    return {};
  }

  const ids = currentFilters.attendingSchoolIds ?? [];
  if (ids.length === 0) {
    return resetIndividualSchoolSelection();
  }

  if (!districtSchools?.length) {
    return {};
  }

  const validIds = new Set(districtSchools.map((school) => school.id));
  const hasValidSelection = ids.some(
    (id) =>
      validIds.has(id) ||
      id === NO_ATTENDANCE_SCHOOL_INFO_SENTINEL ||
      getOrphanAttendanceSchoolInternalId(id) !== null,
  );
  if (!hasValidSelection) {
    return resetIndividualSchoolSelection();
  }

  return {};
}

function resolveImportPeriodDefault(
  currentFilters: PlottingFiltersFormValues,
  yearOptions: ListboxFieldOption[],
): Partial<PlottingFiltersFormValues> {
  const isCurrentYearValid = isYearValid(currentFilters.importPeriod, yearOptions);
  const shouldDefault =
    (!isCurrentYearValid || currentFilters.importPeriod === PLOTTING_FILTER_NONE_VALUE) &&
    yearOptions.length >= 1;

  if (!shouldDefault) {
    return {};
  }

  const firstReal = yearOptions.find((o) => o.value !== PLOTTING_FILTER_NONE_VALUE);
  return {
    importPeriod: (firstReal?.value as string) ?? PLOTTING_FILTER_NONE_VALUE,
  };
}

function resolveSchoolTypeDefault(
  currentFilters: PlottingFiltersFormValues,
  schoolTypeOptions: ListboxFieldOption[],
): Partial<PlottingFiltersFormValues> {
  const isCurrentSchoolTypeValid = isSchoolTypeValid(currentFilters.schoolType, schoolTypeOptions);
  if (isCurrentSchoolTypeValid || schoolTypeOptions.length === 0) {
    return {};
  }

  return {
    schoolType: (schoolTypeOptions[0]?.value as PlottingSchoolTypeEnum) ?? currentFilters.schoolType,
    attendingSchoolIds: [],
  };
}

function resolveGradeDefaults(
  currentFilters: PlottingFiltersFormValues,
  gradeOptions: PlottingGradeListboxOption[],
): Partial<PlottingFiltersFormValues> {
  const realGradeOptions = gradeOptions.filter((o) => o.value !== PLOTTING_FILTER_NONE_VALUE);
  if (realGradeOptions.length < 1) {
    return {};
  }

  const areCurrentGradesValid = areGradesValid(
    currentFilters.fromGrade,
    currentFilters.toGrade,
    gradeOptions,
  );

  if (!areCurrentGradesValid) {
    return {
      fromGrade: realGradeOptions[0]?.value as string ?? PLOTTING_FILTER_NONE_VALUE,
      toGrade: (realGradeOptions[realGradeOptions.length - 1]?.value as string) ?? PLOTTING_FILTER_NONE_VALUE,
    };
  }

  const needsUpdate: Partial<PlottingFiltersFormValues> = {};
  if (currentFilters.fromGrade === PLOTTING_FILTER_NONE_VALUE) {
    needsUpdate.fromGrade = realGradeOptions[0]?.value as string ?? PLOTTING_FILTER_NONE_VALUE;
  }
  if (currentFilters.toGrade === PLOTTING_FILTER_NONE_VALUE) {
    needsUpdate.toGrade =
      (realGradeOptions[realGradeOptions.length - 1]?.value as string) ?? PLOTTING_FILTER_NONE_VALUE;
  }

  return needsUpdate;
}

/**
 * Computes default filter values based on current filters and available options
 * Replaces invalid or "none" values with appropriate defaults from metadata
 */
export function computeDefaultFilters(
  currentFilters: PlottingFiltersFormValues,
  yearOptions: ListboxFieldOption[],
  schoolTypeOptions: ListboxFieldOption[],
  gradeOptions: PlottingGradeListboxOption[],
  districtSchools?: IDistrictSchoolListItem[] | null,
): Partial<PlottingFiltersFormValues> {
  return {
    ...resolveImportPeriodDefault(currentFilters, yearOptions),
    ...resolveSchoolTypeDefault(currentFilters, schoolTypeOptions),
    ...resolveIndividualSchoolTypeDefaults(currentFilters, districtSchools),
    ...resolveGradeDefaults(currentFilters, gradeOptions),
  };
}
